/**
 * Proxy Runtime (P28 + P30 strip).
 *
 * Live data from proxy `/readyz` + `/metrics` + the catalog server list.
 * The wireframe AdapterMap + EnterpriseChart placeholders + PolicySteps
 * cards were decorative scaffolding; removed.
 */
import { useMemo, type JSX } from 'react';
import { Activity, AlertTriangle, LockKeyhole, Route as RouteIcon, Zap } from 'lucide-react';

import {
  parsePrometheusText,
  useCatalogServers,
  useProxyMetrics,
  useProxyReadyz,
} from '@/api/index.js';
import { DataTable, type DataRow } from '@/components/console/DataTable.js';
import { MetricCard } from '@/components/console/MetricCard.js';
import { PageHeader } from '@/components/console/PageHeader.js';
import { SectionCard } from '@/components/console/SectionCard.js';
import { AsyncShell } from '@/components/console/StateBlocks.js';

export default function Proxy(): JSX.Element {
  const readyz = useProxyReadyz();
  const metrics = useProxyMetrics();
  const servers = useCatalogServers();

  const parsed = useMemo(
    () => (metrics.data ? parsePrometheusText(metrics.data) : []),
    [metrics.data],
  );
  const totalRequests = parsed
    .filter((m) => m.name === 'http_requests_total')
    .reduce((s, m) => s + m.value, 0);
  const mcpRequests = parsed
    .filter((m) => m.name === 'http_requests_total' && m.labels.route === '/mcp')
    .reduce((s, m) => s + m.value, 0);
  const errorRequests = parsed
    .filter(
      (m) => m.name === 'http_requests_total' && m.labels.status && Number(m.labels.status) >= 500,
    )
    .reduce((s, m) => s + m.value, 0);
  const successRate =
    totalRequests > 0
      ? `${(((totalRequests - errorRequests) * 100) / totalRequests).toFixed(2)}%`
      : '—';
  const durationSum = parsed
    .filter((m) => m.name === 'http_request_duration_ms')
    .reduce((s, m) => s + m.value, 0);
  const avgLatency = totalRequests > 0 ? `${(durationSum / totalRequests).toFixed(1)} ms` : '—';

  const routeRows: DataRow[] =
    servers.data?.items.map((s) => ({
      slug: s.slug,
      route: '/mcp',
      upstream: s.backendUrl,
      backend_type: s.backendType,
      health: s.status === 'active' ? 'healthy' : s.status,
      risk: s.riskTier,
    })) ?? [];

  return (
    <>
      <PageHeader
        eyebrow="Build"
        title="Proxy Runtime"
        description="Live state of the gateway proxy — health, request totals, and resolved backend routes from the registry."
      />

      <AsyncShell
        isLoading={readyz.isLoading}
        isError={readyz.isError}
        isEmpty={!readyz.data}
        error={readyz.error}
        loadingTitle="Loading proxy state"
        errorTitle="Proxy /readyz unreachable"
        errorHint={<>Proxy unreachable. Hard-refresh; if it persists the gateway is down.</>}
      >
        <div className="metric-grid">
          <MetricCard
            icon={Activity}
            label="Total requests"
            value={totalRequests > 0 ? totalRequests.toLocaleString() : '0'}
            delta={`${mcpRequests} on /mcp`}
            tone="good"
          />
          <MetricCard
            icon={Zap}
            label="Avg latency"
            value={avgLatency}
            delta="all routes"
            tone="neutral"
          />
          <MetricCard
            icon={AlertTriangle}
            label="5xx errors"
            value={String(errorRequests)}
            delta={`${successRate} success`}
            tone={errorRequests > 0 ? 'warn' : 'good'}
          />
          <MetricCard
            icon={LockKeyhole}
            label="Auth check"
            value={readyz.data?.checks.auth ?? '—'}
            delta={`db: ${readyz.data?.checks.db ?? '—'}`}
            tone={readyz.data?.checks.auth === 'wired' ? 'good' : 'warn'}
          />
        </div>

        <p className="subtle" style={{ fontSize: 14, marginBottom: 12 }}>
          Refreshes every 10s. Source: proxy <code>/readyz</code> + <code>/metrics</code>.
        </p>

        <SectionCard
          icon={RouteIcon}
          title="Resolved backend routes"
          text="Each registered server is reachable through the proxy. The proxy forwards JSON-RPC envelopes to the upstream URL based on the resolved tool."
        >
          {routeRows.length === 0 ? (
            <p className="subtle" style={{ fontSize: 15 }}>
              No backends registered yet. Use Register Server to add the first one.
            </p>
          ) : (
            <DataTable
              columns={['slug', 'route', 'upstream', 'backend_type', 'health', 'risk']}
              rows={routeRows}
            />
          )}
        </SectionCard>
      </AsyncShell>
    </>
  );
}
